'use client';

import React, { useEffect, useMemo, useState } from 'react';
import styles from './articleSentiment.module.scss';
import YesIcon from '@/components/icons/yes.svg';
import NoIcon from '@/components/icons/no.svg';
import OHCContext from '@/providers/OHCContext';
import { useTrack } from '@chewy/use-analytics';
import ContactUsButtonPanel from './ContactUsButtonPanel';
import { isRXTopicSelected } from '@/utils/helpCenterUtils';
import { KibButtonNew } from '@chewy/kib-controls-react';

interface ArticleSentimentProps {
  articleId?: string | null;
  articleTitle?: string | null;
}

const ArticleSentiment: React.FC<ArticleSentimentProps> = ({ articleId, articleTitle }) => {
  const { selectedTopic, categoryIdsMap } = React.useContext(OHCContext);
  const [selectedResponse, setSelectedResponse] = useState<boolean | null>(null);
  const isRXTopic = useMemo(() => isRXTopicSelected(selectedTopic?.categoryId, categoryIdsMap), [selectedTopic, categoryIdsMap]);
  const track = useTrack();
  const handleResponse = (response: boolean) => {
    setSelectedResponse(response);

    track(`${response ? 'Positive' : 'Negative'} ${isRXTopic ? 'RX' : ''} Feedback For Article${articleTitle ? ` - ${articleTitle}` : ''}`, {
      id: 'article-sentiment-id',
      parentId: 'article-sentiment-id',
      level: 'article-sentiment',
      type: 'static',
      position: 1,
      event_name: 'Button Clicked',
      eventAction: 'click',
      eventCategory: 'help-center-feedback',
      eventLabel: `help-center-feedback-${response ? 'positive' : 'negative'}-${isRXTopic ? 'rx-' : ''}click`,
      articleId: articleId,
      articleTitle: articleTitle
    });

    track(`${response ? 'Positive' : 'Negative'} ${isRXTopic ? 'RX' : ''} Resolution For Article${articleTitle ? ` - ${articleTitle}` : ''}`, {
      id: 'article-sentiment-id',
      parentId: 'article-sentiment-id',
      level: 'article-sentiment',
      type: 'static',
      position: 1,
      event_name: 'Widget Viewed',
      eventAction: 'impression',
      eventCategory: 'help-center-feedback',
      eventLabel: `help-center-feedback-${response ? 'positive' : 'negative'}-${isRXTopic ? 'rx-' : ''}resolution-view`,
      articleId: articleId,
      articleTitle: articleTitle
    });
  };

  useEffect(() => {
    track(`Article Feedback Viewed${articleTitle ? ` - ${articleTitle}` : ''}`, {
      id: 'article-sentiment-id',
      parentId: 'article-sentiment-id',
      level: 'article-sentiment',
      type: 'static',
      position: 1,
      event_name: 'Widget Viewed',
      eventAction: 'impression',
      eventCategory: 'help-center-feedback',
      eventLabel: 'help-center-feedback-view',
      articleId: articleId,
      articleTitle: articleTitle
    });
  }, []);

  return (
    <div className={styles.feedbackContainer} aria-live="polite">
      {selectedResponse === true ? (
        <p className={styles.thankYouMessage}>Thank you for your feedback!</p>
      ) : selectedResponse === false ? (
        <div>
          <span>{`Thank you—we’ll use your feedback to improve. If you need to contact us, ${isRXTopic ? 'our pharmacy team is here 24/7.' : 'we’re here 24/7.'}`}</span>
          <ContactUsButtonPanel analyticsContext="article-sentiment" articleId={articleId || ''} articleTitle={articleTitle || ''} />
        </div>
      ) : (
        <>
          <p>Did this article answer your question?</p>
          <div className={styles.buttonContainer}>
            <KibButtonNew
              data-testid="sentimentYesButton"
              emphasis="tertiary"
              size="small"
              icon={<YesIcon />}
              onClick={() => handleResponse(true)}
              disabled={selectedResponse !== null}
            >
              Yes
            </KibButtonNew>
            <KibButtonNew
              data-testid="sentimentNoButton"
              emphasis="tertiary"
              size="small"
              icon={<NoIcon />}
              onClick={() => handleResponse(false)}
              disabled={selectedResponse !== null}
            >
              No
            </KibButtonNew>
          </div>
        </>
      )}
    </div>
  );
};

export default ArticleSentiment;
